import {createApi} from '@reduxjs/toolkit/query/react';
import baseQuery from 'src/redux/api/baseQuery';
import { EstadoAspirante } from 'src/utils/constantes';
import { IasignarGestorRequest, IdetalleAspiranteResponse, IEditarHojaDeVidaRequest, IguardarEstadoAspirante, IguardarVerificacionHojaDeVida, IobtenerAspirantesRequest, IobtenerAspirantesResponse, IobtenerEstadosDisponiblesRequest, IobtenerVerificacionesRequest, IregistroHojaDeVidaRequest, VerificacionResponse } from './types/aspirantes';

/**
 * API para la gestión de aspirantes
 * @see https://redux-toolkit.js.org/rtk-query/overview
 * @see https://redux-toolkit.js.org/rtk-query/usage/customizing-queries
 *
 */
export const aspirantesApi = createApi({
	reducerPath: 'aspirantesApi',
	baseQuery,
	tagTypes: ['aspirantes', 'aspirante', 'estados'],
	endpoints: (builder) => ({
		obtenerAspirantes: builder.query<IobtenerAspirantesResponse, IobtenerAspirantesRequest>({
			query({ paginador, ...rest}) {
				const filteredParams = Object.fromEntries(
					Object.entries({ ...rest, ...paginador }).filter(
					  ([, value]) => value !== null && value !== undefined && value !== ''
					)
				  );
				return {
					url: 'aspirantes',
					method: 'GET',
					credentials: 'include',
					params: filteredParams,
				};
			},
			providesTags: ['aspirantes']
		}),
		obtenerAspirantePorId: builder.query<IdetalleAspiranteResponse, string | undefined>({
			query(aspiranteId) {
				return {
					url: `aspirantes/${aspiranteId}`,
					method: 'GET',
					credentials: 'include',
				};
			},
			providesTags: ['aspirante']
		}),
		asignarGestor: builder.mutation<void, IasignarGestorRequest>({
			query({usuarioId, aspiranteId}) {
				return {
					url: `aspirantes/${aspiranteId}/asignar-usuario/${usuarioId}`,
					method: 'PATCH',
					credentials: 'include',
				};
			},
		}),
		registrarHojaDeVida: builder.mutation<void, IregistroHojaDeVidaRequest>({
			query({informacion}) {
				return {
					url: `aspirantes`,
					method: 'POST',
					body: {...informacion},
					credentials: 'include',
				};
			},
		}),
		editarRegistroHojaDeVida: builder.mutation<void, IEditarHojaDeVidaRequest>({
			query({aspiranteId, informacion, token, actualizacionDocente}) {
				return {
					url: `aspirantes/${aspiranteId}`,
					method: 'PUT',
					body: actualizacionDocente?{ actualizacionDocente, ...informacion}: {...informacion},
					credentials: 'include',
					headers: token ? {
						Authorization: `Bearer ${token}`,
					} : undefined
				};
			},

			invalidatesTags: ['aspirante', 'aspirantes']
		}),
		obtenerVerificacionesDeAspirante: builder.query<VerificacionResponse[], IobtenerVerificacionesRequest>({
			query({ aspiranteId, token}) {
				return {
					url: 'verificaciones',
					method: 'GET',
					credentials: 'include',
					params: {aspiranteId},
					headers: token ? {
						Authorization: `Bearer ${token}`,
					} : undefined
				};
			},
		}),
		obtenerEstadosDisponiblesDeAspirante: builder.query<{estadosDisponibles: EstadoAspirante[]}, IobtenerEstadosDisponiblesRequest>({
			query({ aspiranteId}) {
				return {
					url: `aspirantes/${aspiranteId}/estados-disponibles`,
					method: 'GET',
					credentials: 'include',
				};
			},
			providesTags: ['estados']
		}),
		guardarVerificaciones: builder.mutation<void, IguardarVerificacionHojaDeVida>({
			query({verificaciones}) {
				return {
					url: `verificaciones`,
					method: 'PUT',
					body: [...verificaciones],
					credentials: 'include',
				};
			},
			invalidatesTags: ['estados', 'aspirantes']
		}),
		actualizarEstadoAspirante: builder.mutation<void, IguardarEstadoAspirante>({
			query({aspiranteId, estado, observacionRechazo}) {
				return {
					url: `aspirantes/${aspiranteId}/estado`,
					method: 'PATCH',
					body: {estado, observacionRechazo},
					credentials: 'include',
				};
			},
			invalidatesTags: ['estados', 'aspirantes', 'aspirante']
		}),
	}),
});

export const {
	useLazyObtenerAspirantesQuery,
	useObtenerAspirantesQuery,
	useObtenerAspirantePorIdQuery,
	useLazyObtenerAspirantePorIdQuery,
	useAsignarGestorMutation,
	useObtenerVerificacionesDeAspiranteQuery,
	useLazyObtenerVerificacionesDeAspiranteQuery,
	useRegistrarHojaDeVidaMutation,
	useEditarRegistroHojaDeVidaMutation,
	useActualizarEstadoAspiranteMutation,
	useGuardarVerificacionesMutation,
	useObtenerEstadosDisponiblesDeAspiranteQuery
} = aspirantesApi;
